import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useDroits } from '../auth';
import { useAction, useApi } from '../hooks';
import {
  Champ,
  ChampsMachine,
  Chargement,
  ChoixLogiciels,
  MessageErreur,
  Modal,
  Statut,
  StatutBool,
  demain,
  formatDate,
} from '../components/ui';
import { FichePoste } from './PosteDetail';

/** Page /clients/:id */
export default function ClientDetail() {
  const { id } = useParams();
  return <ContenuClient id={id} />;
}

/** Fiche client ouverte en fenêtre par-dessus une liste */
export function FicheClient({ id, onFermer }) {
  return <ContenuClient id={id} onFermer={onFermer} />;
}

function ContenuClient({ id, onFermer }) {
  const enFenetre = Boolean(onFermer);
  const { admin, support } = useDroits();
  const navigate = useNavigate();
  const client = useApi(`/clients/${id}`);
  const resume = useApi(`/clients/${id}/logiciels`);
  const action = useAction();
  const [modal, setModal] = useState(null);
  const [message, setMessage] = useState(null);
  const [posteOuvert, setPosteOuvert] = useState(null);

  if (client.erreur || !client.donnees) {
    const etat = client.erreur ? <MessageErreur erreur={client.erreur} /> : <Chargement />;
    return enFenetre ? (
      <Modal titre="Fiche client" onFermer={onFermer} classe="modal-fiche">
        {etat}
      </Modal>
    ) : (
      etat
    );
  }
  const c = client.donnees;

  const recharger = () => {
    client.recharger();
    resume.recharger();
  };

  // Autorise et lance la mise à jour vers la dernière version sur tous les postes du client
  const lancer = (logiciel) => {
    if (!window.confirm(`Lancer la mise à jour de ${logiciel.name} sur tous les postes de ${c.name} ?`)) return;
    setMessage(null);
    action.executer(async () => {
      const r = await api(`/clients/${id}/logiciels/${logiciel.id}/mettre-a-jour`, { methode: 'POST' });
      setMessage(
        `${logiciel.name} ${r.version} : ${r.planifiees} poste(s) lancé(s)` +
          (r.dejaEnAttente ? `, ${r.dejaEnAttente} déjà en attente` : '') +
          (r.dejaAJour ? `, ${r.dejaAJour} déjà à jour` : '') +
          '.',
      );
      recharger();
    });
  };

  const titre = (
    <>
      {c.name} <span className="muted">· {c.code}</span>
    </>
  );
  // Suppression possible seulement pour un client sans poste
  const supprimer = () => {
    const postes = c.statistiques.nombrePostes;
    if (postes > 0) {
      window.alert(
        `Impossible de supprimer ${c.name} : il a encore ${postes} poste(s).
` +
          'Supprimez ses postes ou rattachez-les à un autre client.',
      );
      return;
    }
    if (!window.confirm(`Supprimer définitivement le client ${c.name} ?`)) return;
    action.executer(async () => {
      await api(`/clients/${id}`, { methode: 'DELETE' });
      if (onFermer) onFermer();
      else navigate('/clients');
    });
  };

  const boutons = admin && (
    <>
      <button className="btn btn-secondaire" onClick={() => setModal('modifier')}>
        Modifier
      </button>
      <button className="btn btn-danger" disabled={action.enCours} onClick={supprimer}>
        Supprimer
      </button>
    </>
  );

  const contenu = (
    <>
      <div className="tuiles">
        <div className="carte tuile">
          <div className="muted">Postes</div>
          <div className="tuile-valeur">{c.statistiques.nombrePostes}</div>
        </div>
        <div className="carte tuile">
          <div className="muted">Logiciels</div>
          <div className="tuile-valeur">
            {c.statistiques.logicielsPossedes} / {c.statistiques.totalLogiciels}
          </div>
        </div>
        <div className="carte tuile">
          <div className="muted">Mises à jour en attente</div>
          <div className="tuile-valeur">{c.statistiques.misesAJourEnAttente}</div>
        </div>
        <div className="carte tuile">
          <div className="muted">Statut</div>
          <div className="tuile-valeur">
            <StatutBool valeur={c.status} />
          </div>
        </div>
      </div>

      <section className="carte">
        <h2>Informations</h2>
        <dl className="infos">
          <dt>Matricule fiscal</dt>
          <dd>{c.taxIdentifier || '—'}</dd>
          <dt>Convention</dt>
          <dd>{c.conventionne ? 'Conventionné' : 'Non conventionné'}</dd>
          <dt>Email</dt>
          <dd>{c.email || '—'}</dd>
          <dt>Téléphone</dt>
          <dd>{c.phone || '—'}</dd>
          <dt>Adresse</dt>
          <dd>{c.address || '—'}</dd>
          <dt>Créé le</dt>
          <dd>{formatDate(c.createdAt)}</dd>
        </dl>
      </section>

      <section className="carte">
        <h2>Logiciels</h2>
        <MessageErreur erreur={action.erreur || resume.erreur} />
        {message && <div className="alerte alerte-succes">{message}</div>}
        {resume.donnees && (
          <table>
            <thead>
              <tr>
                <th>Logiciel</th>
                <th>Postes équipés</th>
                <th>Version installée (min.)</th>
                <th>Dernière version</th>
                <th>Statut</th>
                <th>Mises à jour autorisées</th>
                {support && <th />}
              </tr>
            </thead>
            <tbody>
              {resume.donnees.map((r) => {
                return (
                  <tr key={r.logiciel.id}>
                    <td>{r.logiciel.name}</td>
                    <td>{r.postesEquipes}</td>
                    <td>{r.versionInstallee || '—'}</td>
                    <td>{r.derniereVersion || '—'}</td>
                    <td>
                      <StatutPostes resume={r} />
                    </td>
                    <td>
                      {r.postesAutorises} / {r.postesEquipes}
                    </td>
                    {support && (
                      <td>
                        {r.postesNonAJour > 0 && (
                          <button className="btn btn-principal btn-petit" disabled={action.enCours} onClick={() => lancer(r.logiciel)}>
                            Lancer la mise à jour
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              {resume.donnees.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    Aucun logiciel installé.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>

      <section className="carte">
        <div className="titre-page">
          <h2>Postes</h2>
          {support && c.status && (
            <button className="btn btn-principal" onClick={() => setModal('poste')}>
              + Ajouter un poste
            </button>
          )}
        </div>
        <table>
          <thead>
            <tr>
              <th>Poste</th>
              <th>Licence</th>
              <th>Logiciels</th>
              <th>Nom machine</th>
              <th>Dernière connexion</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {c.postes.map((p) => (
              <tr key={p.id} className="ligne-cliquable" onClick={() => setPosteOuvert(p.id)}>
                <td>
                  <span className="lien">{p.name}</span>
                </td>
                <td>
                  {p.license.licenseKey}
                </td>
                <td>{p.logiciels.map((l) => l.name).join(', ') || '—'}</td>
                <td>{p.hostname || '—'}</td>
                <td>{formatDate(p.lastConnectionAt, true)}</td>
                <td>
                  <Statut valeur={p.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {modal === 'modifier' && (
        <ModificationClient
          client={c}
          onFermer={() => setModal(null)}
          onModifie={() => {
            setModal(null);
            recharger();
          }}
        />
      )}
      {modal === 'poste' && (
        <AjoutPoste
          clientId={c.id}
          onFermer={() => setModal(null)}
          onAjoute={() => {
            setModal(null);
            recharger();
          }}
        />
      )}
      {posteOuvert && (
        <FichePoste
          id={posteOuvert}
          depuisClient
          onFermer={() => {
            setPosteOuvert(null);
            recharger();
          }}
        />
      )}
    </>
  );

  if (enFenetre) {
    return (
      <Modal titre={titre} libelle={`Fiche client ${c.name}`} actions={boutons} onFermer={onFermer} classe="modal-fiche">
        {contenu}
      </Modal>
    );
  }

  return (
    <>
      <p>
        <Link to="/clients">‹ Clients</Link>
      </p>
      <div className="titre-page">
        <h1>{titre}</h1>
        {boutons && <div className="modal-actions">{boutons}</div>}
      </div>
      {contenu}
    </>
  );
}

function ModificationClient({ client, onFermer, onModifie }) {
  const { executer, enCours, erreur } = useAction();
  const [valeurs, setValeurs] = useState({
    name: client.name,
    taxIdentifier: client.taxIdentifier || '',
    email: client.email || '',
    phone: client.phone || '',
    address: client.address || '',
    status: client.status,
    conventionne: client.conventionne,
  });
  const champ = (cle) => ({ value: valeurs[cle], onChange: (e) => setValeurs({ ...valeurs, [cle]: e.target.value }) });

  const soumettre = (e) => {
    e.preventDefault();
    executer(async () => {
      // Un champ vidé est enregistré comme « non renseigné »
      const corps = Object.fromEntries(
        Object.entries(valeurs).map(([k, v]) => [k, typeof v === 'string' && k !== 'name' && v.trim() === '' ? null : v]),
      );
      await api(`/clients/${client.id}`, { methode: 'PUT', corps });
      onModifie();
    });
  };

  return (
    <Modal titre={`Modifier ${client.name}`} onFermer={onFermer}>
      <form onSubmit={soumettre}>
        <MessageErreur erreur={erreur} />
        <Champ libelle="Nom *">
          <input {...champ('name')} required />
        </Champ>
        <Champ libelle="Matricule fiscal" aide="13 caractères (ex. 1234567ABM000)">
          <input {...champ('taxIdentifier')} minLength={13} maxLength={13} />
        </Champ>
        <Champ libelle="Email">
          <input type="email" {...champ('email')} />
        </Champ>
        <Champ libelle="Téléphone">
          <input {...champ('phone')} />
        </Champ>
        <Champ libelle="Adresse">
          <input {...champ('address')} />
        </Champ>
        <label className="case">
          <input type="checkbox" checked={valeurs.status} onChange={(e) => setValeurs({ ...valeurs, status: e.target.checked })} />
          Client actif
        </label>
        <label className="case">
          <input
            type="checkbox"
            checked={valeurs.conventionne}
            onChange={(e) => setValeurs({ ...valeurs, conventionne: e.target.checked })}
          />
          Client conventionné
        </label>
        <div className="actions-modal">
          <button type="button" className="btn btn-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button className="btn btn-principal" disabled={enCours}>
            Enregistrer
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Création d'un poste. Sans `clientId` (page Postes), le cabinet est choisi dans la liste des clients actifs.
 * `onAjoute` reçoit le poste créé.
 */
export function AjoutPoste({ clientId, onFermer, onAjoute }) {
  const { donnees: logiciels } = useApi('/logiciels');
  const { donnees: clients } = useApi(clientId ? null : '/clients', { actif: 'oui', parPage: 100 });
  const { executer, enCours, erreur } = useAction();
  const [poste, setPoste] = useState({
    clientId: clientId ?? '',
    name: '',
    licenseKey: '',
    expiresAt: '',
    hostname: '',
    operatingSystem: '',
    softwareIds: [],
  });

  const soumettre = (e) => {
    e.preventDefault();
    executer(async () => {
      onAjoute(await api('/postes', { methode: 'POST', corps: { ...poste, expiresAt: poste.expiresAt || undefined } }));
    });
  };

  return (
    <Modal titre="Ajouter un poste" onFermer={onFermer}>
      <form onSubmit={soumettre}>
        <MessageErreur erreur={erreur} />
        {!clientId && (
          <Champ libelle="Cabinet *">
            <select value={poste.clientId} onChange={(e) => setPoste({ ...poste, clientId: e.target.value })} required>
              <option value="">— Choisir le cabinet —</option>
              {clients?.donnees.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code})
                </option>
              ))}
            </select>
          </Champ>
        )}
        <Champ libelle="Nom du poste *">
          <input value={poste.name} onChange={(e) => setPoste({ ...poste, name: e.target.value })} required />
        </Champ>
        <Champ libelle="Clé de licence *" aide="Une licence libérée par un autre poste peut être réutilisée">
          <input value={poste.licenseKey} onChange={(e) => setPoste({ ...poste, licenseKey: e.target.value })} required />
        </Champ>
        <Champ libelle="Expiration de la licence">
          <input type="date" min={demain()} value={poste.expiresAt} onChange={(e) => setPoste({ ...poste, expiresAt: e.target.value })} />
        </Champ>
        <ChampsMachine valeur={poste} onChange={(champs) => setPoste({ ...poste, ...champs })} />
        {logiciels && (
          <ChoixLogiciels logiciels={logiciels} valeur={poste.softwareIds} onChange={(softwareIds) => setPoste({ ...poste, softwareIds })} />
        )}
        <div className="actions-modal">
          <button type="button" className="btn btn-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button className="btn btn-principal" disabled={enCours}>
            Ajouter
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Statut d'un logiciel chez le client : nombre de postes non à jour et en attente, sinon « À jour » */
function StatutPostes({ resume }) {
  if (!resume.postesNonAJour && !resume.postesEnAttente) return <Statut valeur="A_JOUR" />;
  return (
    <>
      {resume.postesNonAJour > 0 && (
        <span className="badge badge-orange">{resume.postesNonAJour} Non à jour</span>
      )}{' '}
      {resume.postesEnAttente > 0 && <span className="badge badge-bleu">{resume.postesEnAttente} En attente</span>}
    </>
  );
}
